import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toEasternDateShort } from '../utils/time';
import { useListFilter } from '../hooks/useListFilter';
import Tabs from '../components/Tabs';
import { PageHeader, StatCard, Panel, StatusPill, Empty, Skeleton, Icon, SortTh, useSort, usePager, Pager, useToast } from '../../components/ui';

const TABS = [['all', 'All comments'], ['pending', 'Pending'], ['replied', 'Replied'], ['escalated', 'Escalated'], ['lead', 'Lead']];

const statusOf = (c) => c.status;
const searchFields = (c) => [c.author_name, c.content, c.classification, c.reply_content];
const cell = { maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

export default function CommentsPage() {
  const [showToast, toastNode] = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(null);
  const { sort, onSort, apply } = useSort('created_at', 'desc');

  const load = useCallback(() =>
    api.getComments()
      .then(r => { setItems(r.items || []); setLastUpdated(new Date()); })
      .catch(() => showToast('Could not load comments', 'error'))
      .finally(() => setLoading(false)), [showToast]);

  useEffect(() => { load(); }, [load]);

  const { counts, filtered } = useListFilter(items, { statusOf, searchFields, tab, q });
  const sorted = apply(filtered, (c, col) => c[col] || '');
  const { pageRows, pagerProps } = usePager(sorted, [tab, q, sort.col, sort.dir]);

  return (
    <>
      <PageHeader
        title="Comments"
        subtitle="Comments on the Facebook Page and how the agent handled each one."
        meta={lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : null}
        actions={<button type="button" className="ops-btn" onClick={load} title="Refresh"><Icon name="refresh" /> Refresh</button>}
      />

      {!loading && items.length > 0 && (
        <div className="ops-kpis ops-kpis--4">
          <StatCard label="Total comments" value={counts.all} />
          <StatCard label="Pending" value={counts.pending || 0} tone={counts.pending > 0 ? 'amber' : undefined} />
          <StatCard label="Escalated" value={counts.escalated || 0} tone={counts.escalated > 0 ? 'red' : undefined} />
          <StatCard label="Replied" value={counts.replied || 0} tone="green" />
        </div>
      )}

      <Panel flush
        title={<Tabs tabs={TABS} active={tab} counts={counts} onChange={setTab} />}
        action={<div className="ops-search"><Icon name="search" /><input className="ops-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search comments" aria-label="Search comments" /></div>}
        foot={!loading && sorted.length > 0 ? <Pager {...pagerProps} label="comments" /> : null}>
        {loading ? <Skeleton rows={6} />
          : items.length === 0 ? <Empty title="No comments yet">Comments appear here as the agent picks them up from the Page.</Empty>
            : sorted.length === 0 ? <Empty title="No matching comments">Nothing matches this search or filter.</Empty> : (
              <div className="ops-table-wrap">
                <table className="ops-table">
                  <thead>
                    <tr>
                      <SortTh col="created_at" label="Date" sort={sort} onSort={onSort} />
                      <SortTh col="author_name" label="From" sort={sort} onSort={onSort} />
                      <th>Comment</th>
                      <th>Classification</th>
                      <SortTh col="status" label="Status" sort={sort} onSort={onSort} />
                      <th>Reply</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(c => (
                      <React.Fragment key={c.comment_id}>
                        <tr className={`is-clickable${expanded === c.comment_id ? ' is-selected' : ''}`}
                          onClick={() => setExpanded(expanded === c.comment_id ? null : c.comment_id)}>
                          <td className="mono muted">{toEasternDateShort(c.created_at)}</td>
                          <td className="ops-cell-main">{c.author_name || 'Unknown'}</td>
                          <td className="muted" style={cell}>{c.content}</td>
                          <td>{c.classification ? <span className="ops-pill ops-pill--neutral">{c.classification}</span> : null}</td>
                          <td><StatusPill status={c.status} /></td>
                          <td style={cell}>{c.reply_content || <span className="muted">No reply</span>}</td>
                        </tr>
                        {expanded === c.comment_id && (
                          <tr>
                            <td colSpan={6} style={{ background: 'var(--surface-2)', padding: '16px 18px' }}>
                              <dl className="ops-dl">
                                <div><dt>Comment</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{c.content}</dd></div>
                                <div><dt>Reply</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{c.reply_content || 'No reply was sent.'}</dd></div>
                              </dl>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </Panel>
      {toastNode}
    </>
  );
}
